/**
 * The 1-5 rating scales, with the source texts' own wording as the option labels.
 *
 * Transcribed from the Apnea Training Book, Ch.2 §2.8-2.10 (recovery, relaxation,
 * technique) and Ch.3 §3.10-3.12 (recovery, relaxation, perceived effort).
 *
 * Recovery is the primary progression signal: the book is explicit that a long
 * hold with poor recovery is not a good repetition, and that falling recovery
 * scores are a reason to hold or reduce rather than progress.
 */

export interface ScaleOption {
  value: number;
  label: string;
  description: string;
}

export interface RatingScale {
  id: 'recovery' | 'relaxation' | 'technique' | 'perceivedEffort';
  name: string;
  /** Why this rating is collected — shown as helper text. */
  purpose: string;
  options: ScaleOption[];
  citation: string;
}

export const RECOVERY_SCALE: RatingScale = {
  id: 'recovery',
  name: 'Recovery',
  purpose: 'The most important metric here. A long hold with poor recovery is not a good repetition.',
  options: [
    { value: 1, label: 'Poor', description: 'Recovery felt rushed, uncomfortable, or abnormal.' },
    { value: 2, label: 'Below average', description: 'Manageable but noticeably strained.' },
    { value: 3, label: 'Good', description: 'Comfortable and controlled.' },
    { value: 4, label: 'Very good', description: 'Calm and predictable.' },
    { value: 5, label: 'Excellent', description: 'Effortless and completely controlled.' },
  ],
  citation: 'Apnea Training Book Ch.2 §2.8 / Ch.3 §3.10',
};

export const RELAXATION_SCALE: RatingScale = {
  id: 'relaxation',
  name: 'Relaxation',
  purpose: 'The aim is for the same training stimulus to gradually feel calmer.',
  options: [
    { value: 1, label: 'Very tense', description: 'Significant physical or mental tension.' },
    { value: 2, label: 'Somewhat tense', description: 'Tension appeared repeatedly.' },
    { value: 3, label: 'Moderate', description: 'Mostly relaxed with occasional tension.' },
    { value: 4, label: 'Relaxed', description: 'Good control with little unnecessary tension.' },
    { value: 5, label: 'Highly relaxed', description: 'Smooth and effortless throughout.' },
  ],
  citation: 'Apnea Training Book Ch.2 §2.9 / Ch.3 §3.11',
};

export const TECHNIQUE_SCALE: RatingScale = {
  id: 'technique',
  name: 'Technique',
  purpose: 'Quality of movement and execution, independent of duration.',
  options: [
    { value: 1, label: 'Poor', description: 'Obviously strained or inconsistent.' },
    { value: 2, label: 'Needs work', description: 'Repeated technique problems.' },
    { value: 3, label: 'Acceptable', description: 'Basic technique remained controlled.' },
    { value: 4, label: 'Good', description: 'Smooth and consistent.' },
    { value: 5, label: 'Excellent', description: 'Calm, efficient, and repeatable.' },
  ],
  citation: 'Apnea Training Book Ch.2 §2.10',
};

export const EFFORT_SCALE: RatingScale = {
  id: 'perceivedEffort',
  name: 'Perceived effort',
  purpose: 'If every session feels like a 5, you are testing more than you are training.',
  options: [
    { value: 1, label: 'Very easy', description: '' },
    { value: 2, label: 'Easy', description: '' },
    { value: 3, label: 'Moderate', description: '' },
    { value: 4, label: 'Challenging', description: '' },
    { value: 5, label: 'Very challenging', description: '' },
  ],
  citation: 'Apnea Training Book Ch.3 §3.12',
};

export const ALL_SCALES: RatingScale[] = [
  RECOVERY_SCALE,
  RELAXATION_SCALE,
  TECHNIQUE_SCALE,
  EFFORT_SCALE,
];

/**
 * Progression decision, using the book's own three-way framing. Deliberately not
 * a binary: "repeat" is a legitimate and common outcome, not a failure to progress.
 */
export const PROGRESSION_OPTIONS: Array<{
  value: 'Repeat' | 'Reduce' | 'Progress';
  label: string;
  description: string;
}> = [
  {
    value: 'Repeat',
    label: 'Repeat this level',
    description: 'Run the same workload again. The most common correct answer.',
  },
  {
    value: 'Reduce',
    label: 'Reduce',
    description: 'Back off. Appropriate after poor recovery, warning signs, or a rushed session.',
  },
  {
    value: 'Progress',
    label: 'Progress',
    description: 'Only when the current level is calm, controlled and repeatable.',
  },
];

/** One-Skill Rule suggestions, from the book's own examples. */
export const FOCUS_SUGGESTIONS: string[] = [
  'Relaxed shoulders',
  'Relaxed jaw',
  'Relaxed face',
  'Smooth recovery breathing',
  'Consistent posture',
  'Mental composure',
  'Awareness of tension',
  'Consistent table execution',
];
